import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { SignaturePad, type SignaturePadValue } from '@/components/SignaturePad';
import {
  createTechDiagnosticTestResult,
  getTechDiagnosticSession,
  listTechDiagnosticTestResults,
  TechDiagnosticSession,
  TechDiagnosticSessionStatus,
  TechDiagnosticTestResult,
  updateTechDiagnosticSession,
} from '@/lib/tech-api-client';

type DiagnosticStep =
  | 'setup'
  | 'interview'
  | 'authorization'
  | 'safety_gate'
  | 'equipment'
  | 'test_total_external_static_pressure'
  | 'test_temperature_split'
  | 'options'
  | 'summary';

type InterviewPmRecency = 'yes' | 'no' | 'not_sure' | '';

type OptionSelected = 'option_1' | 'option_2' | 'option_3' | 'option_4' | 'declined' | '';

type IaqTriggerId = 'allergies_asthma' | 'musty_smell' | 'visible_dust' | 'pets' | 'smokers';

const STEPS: Array<{ id: DiagnosticStep; label: string }> = [
  { id: 'setup', label: 'Setup' },
  { id: 'interview', label: 'Interview' },
  { id: 'authorization', label: 'Authorization' },
  { id: 'safety_gate', label: 'Safety Gate' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'test_total_external_static_pressure', label: 'Static Pressure' },
  { id: 'test_temperature_split', label: 'Temperature Split' },
  { id: 'options', label: 'Options 1–4' },
  { id: 'summary', label: 'Summary' },
];

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function formatSessionStatus(raw: TechDiagnosticSessionStatus): { label: string; className: string } {
  if (raw === 'completed') return { label: 'Completed', className: 'bg-green-100 text-green-800 border-green-200' };
  if (raw === 'hard_stop') return { label: 'Hard Stop', className: 'bg-red-100 text-red-800 border-red-200' };
  if (raw === 'abandoned') return { label: 'Abandoned', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  return { label: 'In progress', className: 'bg-blue-100 text-blue-800 border-blue-200' };
}

function formatOptionSelectedLabel(value: OptionSelected): string {
  if (value === 'option_1') return 'Option 1';
  if (value === 'option_2') return 'Option 2';
  if (value === 'option_3') return 'Option 3';
  if (value === 'option_4') return 'Option 4';
  if (value === 'declined') return 'Declined';
  return '—';
}

function Chip({ label, className }: { label: string; className: string }) {
  return (
    <span className={['inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium', className].join(' ')}>
      {label}
    </span>
  );
}

function formatNumber(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim()) return value;
  return '';
}

function parseNumber(value: string): number | null {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return null;
  return num;
}

function getAgeYears(session: TechDiagnosticSession | null): number | null {
  if (!session) return null;
  const raw = (session.equipment || {}) as Record<string, unknown>;
  const age = raw.age_years;
  if (typeof age === 'number' && Number.isFinite(age)) return age;
  if (typeof age === 'string') {
    const n = Number(age);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function getLatestTestResult(results: TechDiagnosticTestResult[], moduleId: string): TechDiagnosticTestResult | null {
  const filtered = results.filter((r) => String(r.test_module_id || '').trim() === moduleId);
  if (filtered.length === 0) return null;
  return filtered[filtered.length - 1];
}

const UPGRADE_CATALOG: Array<{ id: string; label: string }> = [
  { id: 'smart_thermostat', label: 'Smart thermostat' },
  { id: 'uv_light_system', label: 'UV light system' },
  { id: 'media_filter_upgrade', label: 'Media filter upgrade' },
  { id: 'surge_protector', label: 'Surge protector' },
  { id: 'duct_sealing', label: 'Duct sealing / airflow improvements' },
  { id: 'whole_home_dehumidifier', label: 'Whole-home dehumidifier' },
];

const INTERVIEW_SYMPTOMS: Array<{ id: string; label: string }> = [
  { id: 'not_cooling', label: 'Not cooling' },
  { id: 'not_heating', label: 'Not heating' },
  { id: 'noise', label: 'Noise' },
  { id: 'odor', label: 'Odor' },
  { id: 'leaking_water', label: 'Leaking water' },
  { id: 'short_cycling', label: 'Short cycling' },
  { id: 'uneven_temps', label: 'Uneven temps' },
  { id: 'high_bills', label: 'High bills' },
  { id: 'wont_turn_on', label: 'Won\'t turn on' },
  { id: 'thermostat_issue', label: 'Thermostat issue' },
];

const INTERVIEW_PRIORITIES: Array<{ id: string; label: string }> = [
  { id: 'lowest_cost_today', label: 'Lowest cost today' },
  { id: 'reliability', label: 'Reliability' },
  { id: 'comfort', label: 'Comfort' },
  { id: 'air_quality', label: 'Air quality' },
  { id: 'energy_savings', label: 'Energy savings' },
  { id: 'quiet', label: 'Quiet' },
];

const IAQ_TRIGGER_CATALOG: Array<{ id: IaqTriggerId; label: string }> = [
  { id: 'allergies_asthma', label: 'Allergies / asthma' },
  { id: 'musty_smell', label: 'Musty smell' },
  { id: 'visible_dust', label: 'Visible dust' },
  { id: 'pets', label: 'Pets' },
  { id: 'smokers', label: 'Smokers' },
];

export default function TechDiagnosticSessionPage() {
  const { id } = useParams();
  const sessionId = String(id || '').trim();
  const { accessToken } = useTechAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState<TechDiagnosticSession | null>(null);
  const [testResults, setTestResults] = useState<TechDiagnosticTestResult[]>([]);

  const [step, setStep] = useState<DiagnosticStep>('setup');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Setup
  const [photoConsent, setPhotoConsent] = useState(false);

  // Interview (Thermostat Interview)
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [symptomStartedWhen, setSymptomStartedWhen] = useState('');
  const [recentPowerEvent, setRecentPowerEvent] = useState<boolean | null>(null);
  const [priorRepairs, setPriorRepairs] = useState<boolean | null>(null);
  const [filterLastChanged, setFilterLastChanged] = useState('');
  const [pmWithinLast12Months, setPmWithinLast12Months] = useState<InterviewPmRecency>('');
  const [customerPriorities, setCustomerPriorities] = useState<string[]>([]);
  const [iaqTriggers, setIaqTriggers] = useState<Record<IaqTriggerId, boolean>>({
    allergies_asthma: false,
    musty_smell: false,
    visible_dust: false,
    pets: false,
    smokers: false,
  });
  const [systemAgeEstimate, setSystemAgeEstimate] = useState('');
  const [membershipOpenness, setMembershipOpenness] = useState<boolean | null>(null);

  // Diagnostic Authorization (pre-work)
  const [diagnosticFeeNonMemberText, setDiagnosticFeeNonMemberText] = useState('99');
  const [diagnosticFeeMemberText, setDiagnosticFeeMemberText] = useState('69');
  const [applyMembershipForDiagnosticDiscount, setApplyMembershipForDiagnosticDiscount] = useState(false);
  const [authorizationAcknowledged, setAuthorizationAcknowledged] = useState(false);
  const [authorizationPermission, setAuthorizationPermission] = useState(false);
  const [authorizationSignedName, setAuthorizationSignedName] = useState('');
  const [authorizationSignatureDraft, setAuthorizationSignatureDraft] = useState<SignaturePadValue>({
    dataUrl: '',
    isEmpty: true,
  });
  const [authorizationSavedSignatureDataUrl, setAuthorizationSavedSignatureDataUrl] = useState('');
  const [authorizationSavedSignedAt, setAuthorizationSavedSignedAt] = useState<string | null>(null);

  // Safety Gate
  const [gasOdor, setGasOdor] = useState<boolean | null>(null);
  const [coConcern, setCoConcern] = useState<boolean | null>(null);
  const [electricalHazard, setElectricalHazard] = useState<boolean | null>(null);
  const [waterEmergency, setWaterEmergency] = useState<boolean | null>(null);
  const [lotoConfirmed, setLotoConfirmed] = useState(false);
  const [hazardsMitigated, setHazardsMitigated] = useState(false);

  // Equipment
  const [ageYearsText, setAgeYearsText] = useState('');

  // Static pressure
  const [ratedEspText, setRatedEspText] = useState('0.5');
  const [supplyStaticText, setSupplyStaticText] = useState('');
  const [returnStaticText, setReturnStaticText] = useState('');
  const [staticPressureNotes, setStaticPressureNotes] = useState('');

  // Temp split
  const [tempMode, setTempMode] = useState<'cooling' | 'heating'>('cooling');
  const [returnAirTempText, setReturnAirTempText] = useState('');
  const [supplyAirTempText, setSupplyAirTempText] = useState('');
  const [tempSplitNotes, setTempSplitNotes] = useState('');

  // Options builder (Option 1/2/3/4)
  const [selectedUpgrades, setSelectedUpgrades] = useState<string[]>([]);

  const [option1Title, setOption1Title] = useState('Option 1 — Band-Aid + PM (Non-member)');
  const [option1TotalText, setOption1TotalText] = useState('');
  const [option1Notes, setOption1Notes] = useState('');

  const [option2Title, setOption2Title] = useState('Option 2 — Same scope with Comfort Club savings');
  const [option2TotalText, setOption2TotalText] = useState('');
  const [option2MembershipPaymentText, setOption2MembershipPaymentText] = useState('');
  const [option2Notes, setOption2Notes] = useState('');

  const [option3Title, setOption3Title] = useState('Option 3 — Member plan + Future-proof bundle');
  const [option3TotalText, setOption3TotalText] = useState('');
  const [option3MembershipPaymentText, setOption3MembershipPaymentText] = useState('');
  const [option3Notes, setOption3Notes] = useState('');

  const [option4Title, setOption4Title] = useState('Option 4 — Best / Everything Recommended');
  const [option4TotalText, setOption4TotalText] = useState('');
  const [option4MembershipPaymentText, setOption4MembershipPaymentText] = useState('');
  const [option4Notes, setOption4Notes] = useState('');

  const [replacementPlanningDiscussed, setReplacementPlanningDiscussed] = useState(false);
  const [replacementPlanningNotes, setReplacementPlanningNotes] = useState('');

  const [optionSelected, setOptionSelected] = useState<OptionSelected>('');

  const statusChip = session ? formatSessionStatus(session.status) : null;
  const ageYears = getAgeYears(session);
  const mustPresentReplacement = ageYears !== null && ageYears >= 8;

  const load = async () => {
    if (!accessToken) return;
    if (!sessionId) return;

    setLoading(true);
    setError(null);
    try {
      const [s, results] = await Promise.all([
        getTechDiagnosticSession(accessToken, sessionId),
        listTechDiagnosticTestResults(accessToken, sessionId),
      ]);

      setSession(s);
      setTestResults(results);

      const stepRaw = String(s.current_step || '').trim();
      const knownStep = STEPS.find((x) => x.id === (stepRaw as DiagnosticStep));
      setStep(knownStep ? (knownStep.id as DiagnosticStep) : 'setup');

      // Hydrate a few form values from the stored session where possible
      const equipment = (s.equipment || {}) as Record<string, unknown>;
      setAgeYearsText(formatNumber(equipment.age_years));

      const preVisit = (s.pre_visit_data || {}) as Record<string, unknown>;
      const consent = (preVisit.consent || {}) as Record<string, unknown>;
      setPhotoConsent(Boolean(consent.photo_consent));

      const auth = (preVisit.diagnostic_authorization || {}) as Record<string, unknown>;
      setDiagnosticFeeNonMemberText(formatNumber(auth.diagnostic_fee_non_member_usd) || '99');
      setDiagnosticFeeMemberText(formatNumber(auth.diagnostic_fee_member_usd) || '69');
      setApplyMembershipForDiagnosticDiscount(Boolean(auth.apply_membership_for_diagnostic_discount));
      setAuthorizationAcknowledged(Boolean(auth.acknowledgement_confirmed));
      setAuthorizationPermission(Boolean(auth.permission_to_access_equipment));
      setAuthorizationSignedName(String(auth.signed_name || ''));
      setAuthorizationSavedSignatureDataUrl(typeof auth.signature_data_url === 'string' ? auth.signature_data_url : '');
      setAuthorizationSavedSignedAt(auth.signed_at ? String(auth.signed_at) : null);
      setAuthorizationSignatureDraft({ dataUrl: '', isEmpty: true });

      const interview = (s.interview_responses || {}) as Record<string, unknown>;
      setSelectedSymptoms(
        Array.isArray(interview.primary_symptoms)
          ? (interview.primary_symptoms.filter((x) => typeof x === 'string') as string[])
          : []
      );
      setSymptomStartedWhen(String(interview.started_when || ''));
      setRecentPowerEvent(typeof interview.recent_power_events === 'boolean' ? interview.recent_power_events : null);
      setPriorRepairs(
        typeof interview.prior_repairs_last_12_months === 'boolean' ? interview.prior_repairs_last_12_months : null
      );
      setFilterLastChanged(String(interview.filter_last_changed || ''));

      const pmRaw = String(interview.pm_within_last_12_months || '').trim();
      setPmWithinLast12Months(pmRaw === 'yes' || pmRaw === 'no' || pmRaw === 'not_sure' ? (pmRaw as InterviewPmRecency) : '');

      setCustomerPriorities(
        Array.isArray(interview.customer_priorities)
          ? (interview.customer_priorities.filter((x) => typeof x === 'string') as string[])
          : []
      );

      if (interview.iaq_triggers && typeof interview.iaq_triggers === 'object' && !Array.isArray(interview.iaq_triggers)) {
        const iaq = interview.iaq_triggers as Record<string, unknown>;
        setIaqTriggers({
          allergies_asthma: Boolean(iaq.allergies_asthma),
          musty_smell: Boolean(iaq.musty_smell),
          visible_dust: Boolean(iaq.visible_dust),
          pets: Boolean(iaq.pets),
          smokers: Boolean(iaq.smokers),
        });
      } else {
        setIaqTriggers({
          allergies_asthma: false,
          musty_smell: false,
          visible_dust: false,
          pets: false,
          smokers: false,
        });
      }

      setSystemAgeEstimate(String(interview.system_age_customer_estimate || ''));
      setMembershipOpenness(typeof interview.membership_openness === 'boolean' ? interview.membership_openness : null);

      if (s.status === 'hard_stop') {
        setHazardsMitigated(false);
      }

      // Options/upgrades
      const upgradesPresented = Array.isArray(s.upgrades_presented) ? s.upgrades_presented : [];
      const presentedIds = upgradesPresented
        .map((u) => {
          if (typeof u === 'string') return u;
          if (u && typeof u === 'object') {
            const obj = u as Record<string, unknown>;
            return typeof obj.id === 'string' ? obj.id : null;
          }
          return null;
        })
        .filter(Boolean) as string[];
      setSelectedUpgrades(presentedIds);

      const rawSelection = String(s.option_selected || '').trim().toLowerCase();
      let normalizedSelection: OptionSelected = '';
      if (rawSelection === 'good' || rawSelection === 'option_1' || rawSelection === 'option1') normalizedSelection = 'option_1';
      if (rawSelection === 'better' || rawSelection === 'option_2' || rawSelection === 'option2') normalizedSelection = 'option_2';
      if (rawSelection === 'best' || rawSelection === 'option_3' || rawSelection === 'option3') normalizedSelection = 'option_3';
      if (rawSelection === 'replacement' || rawSelection === 'option_4' || rawSelection === 'option4') normalizedSelection = 'option_4';
      if (rawSelection === 'declined') normalizedSelection = 'declined';
      setOptionSelected(normalizedSelection);

      const options = (s.options_presented || {}) as Record<string, unknown>;
      const option1 = (options.option_1 || options.option1 || options.good || {}) as Record<string, unknown>;
      const option2 = (options.option_2 || options.option2 || options.better || {}) as Record<string, unknown>;
      const option3 = (options.option_3 || options.option3 || options.best || {}) as Record<string, unknown>;
      const option4 = (options.option_4 || options.option4 || options.replacement || {}) as Record<string, unknown>;
      const replacementPlanning = (options.replacement_planning || options.replacementPlanning || {}) as Record<string, unknown>;

      setOption1Title(String(option1.title || option1Title));
      setOption1TotalText(formatNumber(option1.total_due_today_usd ?? option1.total_usd ?? option1.price_usd));
      setOption1Notes(String(option1.notes || option1Notes));

      setOption2Title(String(option2.title || option2Title));
      setOption2TotalText(formatNumber(option2.total_due_today_usd ?? option2.total_usd ?? option2.price_usd));
      setOption2MembershipPaymentText(formatNumber(option2.membership_first_payment_usd));
      setOption2Notes(String(option2.notes || option2Notes));

      setOption3Title(String(option3.title || option3Title));
      setOption3TotalText(formatNumber(option3.total_due_today_usd ?? option3.total_usd ?? option3.price_usd));
      setOption3MembershipPaymentText(formatNumber(option3.membership_first_payment_usd));
      setOption3Notes(String(option3.notes || option3Notes));

      setOption4Title(String(option4.title || option4Title));
      setOption4TotalText(formatNumber(option4.total_due_today_usd ?? option4.total_usd ?? option4.price_usd));
      setOption4MembershipPaymentText(formatNumber(option4.membership_first_payment_usd));
      setOption4Notes(String(option4.notes || option4Notes));

      setReplacementPlanningDiscussed(Boolean(replacementPlanning.discussed));
      setReplacementPlanningNotes(String(replacementPlanning.notes || ''));
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, sessionId]);

  const patchSession = async (payload: Parameters<typeof updateTechDiagnosticSession>[2]) => {
    if (!accessToken) return null;
    if (!sessionId) return null;

    setSaving(true);
    setError(null);
    try {
      const updated = await updateTechDiagnosticSession(accessToken, sessionId, payload);
      setSession(updated);
      return updated;
    } catch (e) {
      setError(getErrorMessage(e));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const goToStep = async (next: DiagnosticStep, payload?: Parameters<typeof updateTechDiagnosticSession>[2]) => {
    setStep(next);
    await patchSession({ ...(payload || {}), current_step: next });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sessionHeader = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/diagnostics')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Diagnostic Session</h1>
          {statusChip && <Chip label={statusChip.label} className={statusChip.className} />}
        </div>
        <p className="text-gray-600 mt-2">
          Session: <span className="font-mono text-xs">{sessionId}</span>
          {session?.job_number ? ` • Job ${session.job_number}` : ''}
          {session?.location_id ? ` • Location ${session.location_id}` : ''}
        </p>
      </div>

      <button
        type="button"
        onClick={load}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
        disabled={loading}
      >
        <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
        Refresh
      </button>
    </div>
  );

  const stepNav = (
    <div className="flex flex-wrap gap-2">
      {STEPS.map((s) => {
        const active = s.id === step;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => goToStep(s.id)}
            className={[
              'px-3 py-2 rounded-xl text-sm font-medium border transition-colors',
              active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
            ].join(' ')}
            disabled={saving}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );

  const latestStatic = useMemo(
    () => getLatestTestResult(testResults, 'total_external_static_pressure'),
    [testResults]
  );
  const latestTempSplit = useMemo(() => getLatestTestResult(testResults, 'temperature_split'), [testResults]);

  const submitSetup = async () => {
    if (!photoConsent) {
      setError('Photo consent is required before continuing.');
      return;
    }

    const existing = (session?.pre_visit_data || {}) as Record<string, unknown>;
    const existingConsent = (existing.consent || {}) as Record<string, unknown>;

    const nextPreVisit: Record<string, unknown> = {
      ...existing,
      consent: {
        ...existingConsent,
        photo_consent: true,
        photo_consent_confirmed_at: new Date().toISOString(),
      },
    };

    setError(null);
    await goToStep('interview', { pre_visit_data: nextPreVisit });
  };

  const submitInterview = async () => {
    if (selectedSymptoms.length === 0) {
      setError('Select at least one primary symptom before continuing.');
      return;
    }

    if (!symptomStartedWhen.trim()) {
      setError('Please enter when the symptom started.');
      return;
    }

    if (recentPowerEvent === null) {
      setError('Please answer the recent power event question.');
      return;
    }

    if (priorRepairs === null) {
      setError('Please answer the prior repairs question.');
      return;
    }

    if (!filterLastChanged.trim()) {
      setError('Please enter when the filter was last changed.');
      return;
    }

    if (!pmWithinLast12Months) {
      setError('Please confirm whether preventative maintenance was done within the last 12 months.');
      return;
    }

    if (customerPriorities.length === 0) {
      setError('Select at least one customer priority before continuing.');
      return;
    }

    if (!systemAgeEstimate.trim()) {
      setError('Enter the customer\'s estimate of system age (or type "unknown").');
      return;
    }

    const symptomLabels = selectedSymptoms.map(
      (idVal) => INTERVIEW_SYMPTOMS.find((s) => s.id === idVal)?.label || idVal
    );
    const priorityLabels = customerPriorities.map(
      (idVal) => INTERVIEW_PRIORITIES.find((p) => p.id === idVal)?.label || idVal
    );

    const recap = [
      symptomLabels.length ? `Symptoms: ${symptomLabels.join(', ')}` : null,
      symptomStartedWhen.trim() ? `Started: ${symptomStartedWhen.trim()}` : null,
      pmWithinLast12Months ? `PM (12mo): ${pmWithinLast12Months.replace('_', ' ')}` : null,
      priorityLabels.length ? `Priorities: ${priorityLabels.join(', ')}` : null,
    ]
      .filter(Boolean)
      .join(' • ');

    const payload: Record<string, unknown> = {
      primary_symptoms: selectedSymptoms.slice(0),
      started_when: symptomStartedWhen.trim(),
      recent_power_events: recentPowerEvent,
      prior_repairs_last_12_months: priorRepairs,
      filter_last_changed: filterLastChanged.trim(),
      pm_within_last_12_months: pmWithinLast12Months,
      customer_priorities: customerPriorities.slice(0),
      iaq_triggers: { ...iaqTriggers },
      system_age_customer_estimate: systemAgeEstimate.trim(),
      membership_openness: membershipOpenness,
      recap,
      completed_at: new Date().toISOString(),
    };

    setError(null);
    await goToStep('authorization', { interview_responses: payload });
  };

  const submitAuthorization = async () => {
    const feeNonMember = parseNumber(diagnosticFeeNonMemberText);
    const feeMember = parseNumber(diagnosticFeeMemberText);

    if (feeNonMember === null || feeNonMember <= 0) {
      setError('Enter a valid non-member diagnostic fee.');
      return;
    }

    if (feeMember === null || feeMember <= 0) {
      setError('Enter a valid member diagnostic fee.');
      return;
    }

    if (!authorizationAcknowledged) {
      setError('Customer acknowledgement is required before continuing.');
      return;
    }

    if (!authorizationPermission) {
      setError('Permission to access equipment / attic / crawl space is required before continuing.');
      return;
    }

    if (!authorizationSignedName.trim()) {
      setError('Type the customer\'s name for the authorization signature.');
      return;
    }

    if (authorizationSignatureDraft.isEmpty && !authorizationSavedSignatureDataUrl) {
      setError('Customer signature is required before continuing.');
      return;
    }

    const signatureDataUrl = authorizationSignatureDraft.isEmpty ? authorizationSavedSignatureDataUrl : authorizationSignatureDraft.dataUrl;
    const signedAt = new Date().toISOString();

    const diagnosticAuthorization: Record<string, unknown> = {
      diagnostic_fee_non_member_usd: feeNonMember,
      diagnostic_fee_member_usd: feeMember,
      apply_membership_for_diagnostic_discount: applyMembershipForDiagnosticDiscount,
      acknowledgement_confirmed: authorizationAcknowledged,
      permission_to_access_equipment: authorizationPermission,
      signed_name: authorizationSignedName.trim(),
      signature_data_url: signatureDataUrl,
      signed_at: signedAt,
    };

    const existing = (session?.pre_visit_data || {}) as Record<string, unknown>;
    const nextPreVisit: Record<string, unknown> = {
      ...existing,
      diagnostic_authorization: diagnosticAuthorization,
    };

    setAuthorizationSavedSignatureDataUrl(signatureDataUrl);
    setAuthorizationSavedSignedAt(signedAt);
    setAuthorizationSignatureDraft({ dataUrl: '', isEmpty: true });

    setError(null);
    await goToStep('safety_gate', { pre_visit_data: nextPreVisit });
  };

  const submitSafetyGate = async () => {
    if (gasOdor === null || coConcern === null || electricalHazard === null || waterEmergency === null) {
      setError('Please answer all safety questions before continuing.');
      return;
    }

    if (!lotoConfirmed) {
      setError('Lockout/Tagout confirmation is required before proceeding.');
      return;
    }

    const safetyChecks: Record<string, unknown> = {
      gas_odor: gasOdor,
      co_concern: coConcern,
      electrical_hazard: electricalHazard,
      water_emergency: waterEmergency,
      loto_confirmed: lotoConfirmed,
      hazards_mitigated: hazardsMitigated,
      answered_at: new Date().toISOString(),
    };

    if (gasOdor || coConcern) {
      await patchSession({
        status: 'hard_stop',
        safety_checks: safetyChecks,
        safety_gate_passed: false,
        current_step: 'safety_gate',
      });
      setError(null);
      return;
    }

    if ((electricalHazard || waterEmergency) && !hazardsMitigated) {
      setError('Hazard flagged. Confirm mitigation before continuing.');
      return;
    }

    await patchSession({
      safety_checks: safetyChecks,
      safety_gate_passed: true,
    });

    setError(null);
    await goToStep('equipment');
  };

  const saveEquipment = async () => {
    const ageYearsVal = parseNumber(ageYearsText);
    if (ageYearsVal === null || ageYearsVal < 0 || ageYearsVal > 60) {
      setError('Enter a valid system age in years.');
      return;
    }

    const existing = (session?.equipment || {}) as Record<string, unknown>;
    await patchSession({ equipment: { ...existing, age_years: ageYearsVal } });
    setError(null);
    await goToStep('test_total_external_static_pressure');
  };

  const submitStaticPressure = async () => {
    if (!accessToken) return;

    const rated = parseNumber(ratedEspText);
    const supply = parseNumber(supplyStaticText);
    const ret = parseNumber(returnStaticText);

    if (rated === null || rated <= 0) {
      setError('Enter rated max ESP (e.g. 0.5).');
      return;
    }
    if (supply === null || ret === null) {
      setError('Enter both supply and return static pressure readings.');
      return;
    }

    const totalEsp = supply + Math.abs(ret);
    const percentOfRated = (totalEsp / rated) * 100;

    let status: string = 'PASS';
    let message = 'Static pressure is within equipment specification.';

    if (totalEsp > rated && totalEsp <= rated * 1.2) {
      status = 'MARGINAL';
      message = 'Static pressure slightly exceeds rated maximum.';
    } else if (totalEsp > rated * 1.2) {
      status = 'FAIL';
      message = 'Static pressure significantly exceeds rated maximum.';
    }

    const result = await createTechDiagnosticTestResult(accessToken, sessionId, {
      test_module_id: 'total_external_static_pressure',
      inputs: {
        rated_esp_in_wc: rated,
        supply_static_in_wc: supply,
        return_static_in_wc: ret,
      },
      calculations: {
        total_external_static_pressure_in_wc: totalEsp,
        percent_of_rated: percentOfRated,
      },
      status,
      interpretation: {
        message,
      },
      tech_notes: staticPressureNotes || null,
    });

    setTestResults((prev) => [...prev, result]);
    setError(null);
    await goToStep('test_temperature_split');
  };

  const submitTempSplit = async () => {
    if (!accessToken) return;

    const ret = parseNumber(returnAirTempText);
    const supply = parseNumber(supplyAirTempText);

    if (ret === null || supply === null) {
      setError('Enter both return and supply temperatures.');
      return;
    }

    const delta = tempMode === 'cooling' ? ret - supply : supply - ret;

    let status: string = 'PASS';
    let message = 'Temperature split looks reasonable.';

    if (delta < 12) {
      status = 'LOW';
      message = 'Temperature split is low. Consider airflow, charge, or equipment issues.';
    } else if (delta > 26) {
      status = 'HIGH';
      message = 'Temperature split is high. Consider airflow restriction or freezing risk.';
    }

    const result = await createTechDiagnosticTestResult(accessToken, sessionId, {
      test_module_id: 'temperature_split',
      inputs: {
        mode: tempMode,
        return_air_temp_f: ret,
        supply_air_temp_f: supply,
      },
      calculations: {
        temperature_split_f: delta,
      },
      status,
      interpretation: {
        message,
      },
      tech_notes: tempSplitNotes || null,
    });

    setTestResults((prev) => [...prev, result]);
    setError(null);
    await goToStep('options');
  };

  const toggleUpgrade = (idToToggle: string) => {
    setSelectedUpgrades((prev) => {
      if (prev.includes(idToToggle)) {
        return prev.filter((x) => x !== idToToggle);
      }
      return [...prev, idToToggle];
    });
  };

  const saveOptionsAndMaybeComplete = async (markComplete: boolean) => {
    const upgrades = selectedUpgrades
      .map((idVal) => ({ id: idVal, label: UPGRADE_CATALOG.find((u) => u.id === idVal)?.label || idVal }))
      .slice(0);

    const option1Total = parseNumber(option1TotalText);
    const option2Total = parseNumber(option2TotalText);
    const option3Total = parseNumber(option3TotalText);
    const option4Total = parseNumber(option4TotalText);

    const option2MembershipPayment = parseNumber(option2MembershipPaymentText);
    const option3MembershipPayment = parseNumber(option3MembershipPaymentText);
    const option4MembershipPayment = parseNumber(option4MembershipPaymentText);

    const numericFields = [
      { label: 'Option 1 total due today', raw: option1TotalText, parsed: option1Total },
      { label: 'Option 2 total due today', raw: option2TotalText, parsed: option2Total },
      { label: 'Option 2 membership first payment', raw: option2MembershipPaymentText, parsed: option2MembershipPayment },
      { label: 'Option 3 total due today', raw: option3TotalText, parsed: option3Total },
      { label: 'Option 3 membership first payment', raw: option3MembershipPaymentText, parsed: option3MembershipPayment },
      { label: 'Option 4 total due today', raw: option4TotalText, parsed: option4Total },
      { label: 'Option 4 membership first payment', raw: option4MembershipPaymentText, parsed: option4MembershipPayment },
    ];

    for (const field of numericFields) {
      if (field.raw.trim() && field.parsed === null) {
        setError(`${field.label} must be a number.`);
        return;
      }
    }

    if (markComplete) {
      if (!option1Title.trim() || option1Total === null) {
        setError('Option 1 requires a title and total due today.');
        return;
      }

      if (!option2Title.trim() || option2Total === null || option2MembershipPayment === null) {
        setError('Option 2 requires a title, total due today, and membership first payment.');
        return;
      }

      if (!option3Title.trim() || option3Total === null || option3MembershipPayment === null) {
        setError('Option 3 requires a title, total due today, and membership first payment.');
        return;
      }

      if (!option4Title.trim() || option4Total === null || option4MembershipPayment === null) {
        setError('Option 4 requires a title, total due today, and membership first payment.');
        return;
      }

      if (mustPresentReplacement && !replacementPlanningDiscussed) {
        setError('System is 8+ years old. Confirm replacement planning was discussed.');
        return;
      }

      if (!optionSelected) {
        setError('Select which option the homeowner chose (or declined).');
        return;
      }
    }

    const optionsPresented: Record<string, unknown> = {
      option_1: {
        title: option1Title.trim(),
        total_due_today_usd: option1Total,
        notes: option1Notes.trim(),
      },
      option_2: {
        title: option2Title.trim(),
        total_due_today_usd: option2Total,
        membership_first_payment_usd: option2MembershipPayment,
        notes: option2Notes.trim(),
      },
      option_3: {
        title: option3Title.trim(),
        total_due_today_usd: option3Total,
        membership_first_payment_usd: option3MembershipPayment,
        notes: option3Notes.trim(),
      },
      option_4: {
        title: option4Title.trim(),
        total_due_today_usd: option4Total,
        membership_first_payment_usd: option4MembershipPayment,
        notes: option4Notes.trim(),
      },
    };

    if (mustPresentReplacement) {
      optionsPresented.replacement_planning = {
        discussed: replacementPlanningDiscussed,
        notes: replacementPlanningNotes.trim(),
      };
    }

    const payload: Parameters<typeof updateTechDiagnosticSession>[2] = {
      upgrades_presented: upgrades,
      replacement_presented: mustPresentReplacement,
      replacement_quoted: false,
      options_presented: optionsPresented,
      option_selected: optionSelected || null,
    };

    if (markComplete) {
      payload.status = 'completed';
    }

    await patchSession(payload);
    setError(null);

    if (markComplete) {
      await goToStep('summary');
    }
  };

  if (!sessionId) {
    return (
      <div className="p-6 bg-white border border-gray-200 rounded-2xl">
        <div className="text-sm text-red-800 font-medium">Missing diagnostic session id.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sessionHeader}

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Workflow</h2>
          <div className="text-xs text-gray-500">{saving ? 'Saving…' : session?.updated_at ? `Updated ${session.updated_at}` : ''}</div>
        </div>
        {stepNav}
      </div>

      {step === 'setup' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Step 1: Arrival & Consent</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Job #</div>
              <div className="mt-1 font-medium text-gray-900">{session?.job_number || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Location</div>
              <div className="mt-1 font-medium text-gray-900">{session?.location_id || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Safety gate</div>
              <div className="mt-2">
                {session?.safety_gate_passed ? (
                  <Chip label="Passed" className="bg-green-100 text-green-800 border-green-200" />
                ) : (
                  <Chip label="Not yet" className="bg-yellow-100 text-yellow-800 border-yellow-200" />
                )}
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="text-sm text-amber-900 font-semibold">Consent (required)</div>
            <div className="text-sm text-amber-800 mt-1">
              Confirm the homeowner approves photos/videos of equipment for diagnostic documentation.
            </div>
            <label className="mt-3 flex items-center gap-3">
              <input
                type="checkbox"
                checked={photoConsent}
                onChange={(e) => setPhotoConsent(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm text-amber-900 font-medium">Photo consent confirmed</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={submitSetup}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={!photoConsent || saving}
            >
              Continue to Interview
            </button>
          </div>
        </div>
      )}

      {step === 'interview' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Step 2: Thermostat Interview</h2>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="text-sm text-amber-900 font-semibold">Required fields</div>
            <ul className="mt-2 text-sm text-amber-800 list-disc pl-5 space-y-1">
              <li>Primary symptom(s)</li>
              <li>When it started</li>
              <li>Recent power event (yes/no)</li>
              <li>Prior repairs (yes/no)</li>
              <li>Filter last changed</li>
              <li>PM within last 12 months</li>
              <li>Customer priorities</li>
              <li>System age estimate (or "unknown")</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Primary symptom(s)</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {INTERVIEW_SYMPTOMS.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedSymptoms.includes(s.id)}
                    onChange={() =>
                      setSelectedSymptoms((prev) => (prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id]))
                    }
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-gray-900">{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">When did it start?</div>
              <input
                value={symptomStartedWhen}
                onChange={(e) => setSymptomStartedWhen(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. Today, last week, 2 months ago"
              />
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Filter last changed</div>
              <input
                value={filterLastChanged}
                onChange={(e) => setFilterLastChanged(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. 1 month ago"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Recent power event (storm/outage)?</div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setRecentPowerEvent(true)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    recentPowerEvent === true ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setRecentPowerEvent(false)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    recentPowerEvent === false ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  No
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Prior repairs within last 12 months?</div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setPriorRepairs(true)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    priorRepairs === true ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setPriorRepairs(false)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    priorRepairs === false ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  No
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">PM done within last 12 months?</div>
              <select
                value={pmWithinLast12Months}
                onChange={(e) => setPmWithinLast12Months(e.target.value as InterviewPmRecency)}
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm"
              >
                <option value="">Select…</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="not_sure">Not sure</option>
              </select>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">System age (customer estimate)</div>
              <input
                value={systemAgeEstimate}
                onChange={(e) => setSystemAgeEstimate(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. 10 (or unknown)"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Customer priorities (pick at least 1)</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {INTERVIEW_PRIORITIES.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                  <input
                    type="checkbox"
                    checked={customerPriorities.includes(p.id)}
                    onChange={() =>
                      setCustomerPriorities((prev) =>
                        prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]
                      )
                    }
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-gray-900">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">IAQ triggers (optional)</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {IAQ_TRIGGER_CATALOG.map((t) => (
                <label key={t.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                  <input
                    type="checkbox"
                    checked={iaqTriggers[t.id]}
                    onChange={() => setIaqTriggers((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-gray-900">{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Membership openness (optional)</div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setMembershipOpenness(true)}
                className={[
                  'px-3 py-2 rounded-xl border text-sm font-medium',
                  membershipOpenness === true ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                ].join(' ')}
              >
                Open to membership
              </button>
              <button
                type="button"
                onClick={() => setMembershipOpenness(false)}
                className={[
                  'px-3 py-2 rounded-xl border text-sm font-medium',
                  membershipOpenness === false ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                ].join(' ')}
              >
                Not interested
              </button>
              <button
                type="button"
                onClick={() => setMembershipOpenness(null)}
                className={['px-3 py-2 rounded-xl border text-sm font-medium bg-white border-gray-200 hover:bg-gray-50'].join(
                  ' '
                )}
              >
                Not sure
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={submitInterview}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={saving}
            >
              Save & Continue
            </button>
          </div>
        </div>
      )}

      {step === 'authorization' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Step 3: Diagnostic Authorization (pre-work)</h2>

          <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
            <div className="text-sm font-semibold text-gray-900">Diagnostic fee</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500">Non-member fee ($)</div>
                <input
                  value={diagnosticFeeNonMemberText}
                  onChange={(e) => setDiagnosticFeeNonMemberText(e.target.value)}
                  inputMode="decimal"
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
              </div>
              <div>
                <div className="text-xs text-gray-500">Member fee ($)</div>
                <input
                  value={diagnosticFeeMemberText}
                  onChange={(e) => setDiagnosticFeeMemberText(e.target.value)}
                  inputMode="decimal"
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
              </div>
            </div>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={applyMembershipForDiagnosticDiscount}
                onChange={(e) => setApplyMembershipForDiagnosticDiscount(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm text-gray-900 font-medium">Apply membership today to reduce diagnostic?</span>
            </label>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">What the diagnostic includes</div>
            <ul className="mt-2 text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>System health assessment (today’s issue + future risk)</li>
              <li>Photos + documented readings</li>
              <li>Options built in the required Option 1/2/3/4 structure</li>
              <li>Safety-first gating (hard stop for critical hazards)</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
            <div className="text-sm text-amber-900 font-semibold">Acknowledgement (required)</div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={authorizationAcknowledged}
                onChange={(e) => setAuthorizationAcknowledged(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm text-amber-900 font-medium">Customer acknowledges diagnostic fee + scope</span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={authorizationPermission}
                onChange={(e) => setAuthorizationPermission(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm text-amber-900 font-medium">Permission to access equipment / attic / crawl space</span>
            </label>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Customer name (required)</div>
            <input
              value={authorizationSignedName}
              onChange={(e) => setAuthorizationSignedName(e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
              placeholder="Type customer name"
            />
          </div>

          {authorizationSavedSignatureDataUrl ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
              <div className="text-sm font-semibold text-green-900">Signature saved</div>
              <div className="text-sm text-green-800 mt-1">
                {authorizationSavedSignedAt ? `Saved ${authorizationSavedSignedAt}` : 'Saved previously'}
              </div>
              <div className="mt-3 rounded-xl bg-white border border-green-200 p-3">
                <img src={authorizationSavedSignatureDataUrl} alt="Saved signature" className="max-h-32" />
              </div>
              <div className="text-xs text-green-800 mt-2">Signing below will replace the saved signature.</div>
            </div>
          ) : null}

          <SignaturePad heightPx={220} onChange={setAuthorizationSignatureDraft} />

          {authorizationSignatureDraft.isEmpty && !authorizationSavedSignatureDataUrl ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Signature is required to continue.
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={submitAuthorization}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={saving}
            >
              Save & Continue
            </button>
          </div>
        </div>
      )}

      {step === 'safety_gate' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Step 4: Safety Gate (Hard Stop)</h2>

          {session?.status === 'hard_stop' && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="text-red-900 font-semibold">HARD STOP — Do not proceed</div>
              <div className="text-sm text-red-800 mt-1">
                A critical safety condition was flagged. Resolve the hazard and coordinate with your manager before continuing.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Gas odor detected?</div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setGasOdor(true)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    gasOdor === true ? 'bg-red-600 text-white border-red-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setGasOdor(false)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    gasOdor === false ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  No
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-2">Yes = Hard stop.</div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">CO concern / symptoms?</div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setCoConcern(true)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    coConcern === true ? 'bg-red-600 text-white border-red-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setCoConcern(false)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    coConcern === false ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  No
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-2">Yes = Hard stop.</div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Electrical hazard (burning smell/sparking)?</div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setElectricalHazard(true)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    electricalHazard === true ? 'bg-orange-600 text-white border-orange-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setElectricalHazard(false)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    electricalHazard === false ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  No
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-2">If yes, mitigate before continuing.</div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Active flooding / water near electrical?</div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setWaterEmergency(true)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    waterEmergency === true ? 'bg-orange-600 text-white border-orange-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setWaterEmergency(false)}
                  className={[
                    'px-3 py-2 rounded-xl border text-sm font-medium',
                    waterEmergency === false ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >
                  No
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-2">If yes, mitigate before continuing.</div>
            </div>
          </div>

          {(electricalHazard || waterEmergency) && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
              <div className="text-orange-900 font-semibold">Hazard flagged</div>
              <div className="text-sm text-orange-800 mt-1">
                Do not proceed until hazards are isolated and safe.
              </div>
              <label className="mt-3 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={hazardsMitigated}
                  onChange={(e) => setHazardsMitigated(e.target.checked)}
                  className="w-5 h-5"
                />
                <span className="text-sm text-orange-900 font-medium">Hazards mitigated; safe to proceed</span>
              </label>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
            <div className="text-gray-900 font-semibold">Power isolation verification</div>
            <div className="text-sm text-gray-700 mt-1">Required before opening panels or electrical measurements.</div>
            <label className="mt-3 flex items-center gap-3">
              <input
                type="checkbox"
                checked={lotoConfirmed}
                onChange={(e) => setLotoConfirmed(e.target.checked)}
                className="w-5 h-5"
              />
              <span className="text-sm text-gray-900 font-medium">I confirm a power isolation plan is in place (LOTO)</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={submitSafetyGate}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={saving}
            >
              Save Safety Gate
            </button>
          </div>
        </div>
      )}

      {step === 'equipment' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Step 5: Equipment (MVP)</h2>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm font-semibold text-gray-900">System age (years)</div>
            <input
              value={ageYearsText}
              onChange={(e) => setAgeYearsText(e.target.value)}
              inputMode="decimal"
              className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
              placeholder="e.g. 10"
            />
            <div className="text-xs text-gray-500 mt-2">8+ years triggers replacement planning requirements in options.</div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={saveEquipment}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={saving}
            >
              Save & Continue
            </button>
          </div>
        </div>
      )}

      {step === 'test_total_external_static_pressure' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Test: Total External Static Pressure (TESP)</h2>

          {latestStatic && (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
              <div className="text-sm font-semibold text-gray-900">Last saved result</div>
              <div className="text-xs text-gray-600 mt-1">status: {latestStatic.status || '—'}</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Rated max ESP (in WC)</div>
              <input
                value={ratedEspText}
                onChange={(e) => setRatedEspText(e.target.value)}
                inputMode="decimal"
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="0.5"
              />
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Supply static (in WC)</div>
              <input
                value={supplyStaticText}
                onChange={(e) => setSupplyStaticText(e.target.value)}
                inputMode="decimal"
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. 0.25"
              />
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Return static (in WC, negative)</div>
              <input
                value={returnStaticText}
                onChange={(e) => setReturnStaticText(e.target.value)}
                inputMode="decimal"
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. -0.20"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm font-semibold text-gray-900">Tech notes (optional)</div>
            <textarea
              value={staticPressureNotes}
              onChange={(e) => setStaticPressureNotes(e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
              rows={3}
              placeholder="Filter condition, duct issues, etc."
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={submitStaticPressure}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={saving}
            >
              Save Result
            </button>
          </div>
        </div>
      )}

      {step === 'test_temperature_split' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Test: Temperature Split</h2>

          {latestTempSplit && (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
              <div className="text-sm font-semibold text-gray-900">Last saved result</div>
              <div className="text-xs text-gray-600 mt-1">status: {latestTempSplit.status || '—'}</div>
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm font-semibold text-gray-900">Mode</div>
            <select
              value={tempMode}
              onChange={(e) => setTempMode(e.target.value as 'cooling' | 'heating')}
              className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm"
            >
              <option value="cooling">Cooling</option>
              <option value="heating">Heating</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Return air temp (°F)</div>
              <input
                value={returnAirTempText}
                onChange={(e) => setReturnAirTempText(e.target.value)}
                inputMode="decimal"
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. 75"
              />
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900">Supply air temp (°F)</div>
              <input
                value={supplyAirTempText}
                onChange={(e) => setSupplyAirTempText(e.target.value)}
                inputMode="decimal"
                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                placeholder="e.g. 55"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm font-semibold text-gray-900">Tech notes (optional)</div>
            <textarea
              value={tempSplitNotes}
              onChange={(e) => setTempSplitNotes(e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
              rows={3}
              placeholder="Any observations / context"
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={submitTempSplit}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={saving}
            >
              Save Result
            </button>
          </div>
        </div>
      )}

      {step === 'options' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Step: Options Builder (Option 1/2/3/4)</h2>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="text-sm font-semibold text-amber-900">Requirements</div>
            <ul className="mt-2 text-sm text-amber-800 list-disc pl-5 space-y-1">
              <li>Options must be presented in the required Option 1 / Option 2 / Option 3 / Option 4 structure.</li>
              <li>Option 2–4 include Comfort Club savings + first membership payment.</li>
              <li>Systems 8+ years old should include replacement planning per the spec.</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Recommended upgrades (optional)</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {UPGRADE_CATALOG.map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedUpgrades.includes(u.id)}
                    onChange={() => toggleUpgrade(u.id)}
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-gray-900">{u.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">System age</div>
              <div className="mt-1 font-medium text-gray-900">{ageYears === null ? '—' : `${ageYears} years`}</div>
              {mustPresentReplacement && (
                <div className="mt-2">
                  <Chip label="Replacement required" className="bg-red-100 text-red-800 border-red-200" />
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Tests saved</div>
              <div className="mt-1 font-medium text-gray-900">{testResults.length}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Selected option</div>
              <div className="mt-1 font-medium text-gray-900">{formatOptionSelectedLabel(optionSelected)}</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-4">
            <div className="text-sm font-semibold text-gray-900">Option cards</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Option 1 title</div>
                <input
                  value={option1Title}
                  onChange={(e) => setOption1Title(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
                <div className="text-xs text-gray-500 mt-3">Total due today ($)</div>
                <input
                  value={option1TotalText}
                  onChange={(e) => setOption1TotalText(e.target.value)}
                  inputMode="decimal"
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
                <div className="text-xs text-gray-500 mt-3">Notes / included items</div>
                <textarea
                  value={option1Notes}
                  onChange={(e) => setOption1Notes(e.target.value)}
                  rows={4}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Option 2 title</div>
                <input
                  value={option2Title}
                  onChange={(e) => setOption2Title(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-xs text-gray-500">Total due today ($)</div>
                    <input
                      value={option2TotalText}
                      onChange={(e) => setOption2TotalText(e.target.value)}
                      inputMode="decimal"
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Membership first payment ($)</div>
                    <input
                      value={option2MembershipPaymentText}
                      onChange={(e) => setOption2MembershipPaymentText(e.target.value)}
                      inputMode="decimal"
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-3">Notes / included items</div>
                <textarea
                  value={option2Notes}
                  onChange={(e) => setOption2Notes(e.target.value)}
                  rows={4}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Option 3 title</div>
                <input
                  value={option3Title}
                  onChange={(e) => setOption3Title(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-xs text-gray-500">Total due today ($)</div>
                    <input
                      value={option3TotalText}
                      onChange={(e) => setOption3TotalText(e.target.value)}
                      inputMode="decimal"
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Membership first payment ($)</div>
                    <input
                      value={option3MembershipPaymentText}
                      onChange={(e) => setOption3MembershipPaymentText(e.target.value)}
                      inputMode="decimal"
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-3">Notes / included items</div>
                <textarea
                  value={option3Notes}
                  onChange={(e) => setOption3Notes(e.target.value)}
                  rows={4}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Option 4 title</div>
                <input
                  value={option4Title}
                  onChange={(e) => setOption4Title(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-xs text-gray-500">Total due today ($)</div>
                    <input
                      value={option4TotalText}
                      onChange={(e) => setOption4TotalText(e.target.value)}
                      inputMode="decimal"
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Membership first payment ($)</div>
                    <input
                      value={option4MembershipPaymentText}
                      onChange={(e) => setOption4MembershipPaymentText(e.target.value)}
                      inputMode="decimal"
                      className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-3">Notes / included items</div>
                <textarea
                  value={option4Notes}
                  onChange={(e) => setOption4Notes(e.target.value)}
                  rows={4}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white"
                />
              </div>
            </div>

            {mustPresentReplacement && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3">
                <div className="text-sm font-semibold text-red-900">Replacement planning (age 8+)</div>
                <div className="text-sm text-red-800">
                  Per the build spec, include a replacement planning section (or a dedicated replacement card) when the system is 8+ years old.
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={replacementPlanningDiscussed}
                    onChange={(e) => setReplacementPlanningDiscussed(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-red-900 font-medium">Replacement planning discussed</span>
                </label>

                <div>
                  <div className="text-xs text-gray-600">Notes</div>
                  <textarea
                    value={replacementPlanningNotes}
                    onChange={(e) => setReplacementPlanningNotes(e.target.value)}
                    rows={3}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-red-200 bg-white"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="text-sm font-semibold text-gray-900">Outcome</div>
            <div className="text-sm text-gray-600 mt-1">Which option did the homeowner choose?</div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                <input
                  type="radio"
                  name="option_selected"
                  checked={optionSelected === 'option_1'}
                  onChange={() => setOptionSelected('option_1')}
                  className="w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-900">Option 1</span>
              </label>
              <label className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                <input
                  type="radio"
                  name="option_selected"
                  checked={optionSelected === 'option_2'}
                  onChange={() => setOptionSelected('option_2')}
                  className="w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-900">Option 2</span>
              </label>
              <label className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                <input
                  type="radio"
                  name="option_selected"
                  checked={optionSelected === 'option_3'}
                  onChange={() => setOptionSelected('option_3')}
                  className="w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-900">Option 3</span>
              </label>
              <label className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                <input
                  type="radio"
                  name="option_selected"
                  checked={optionSelected === 'option_4'}
                  onChange={() => setOptionSelected('option_4')}
                  className="w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-900">Option 4</span>
              </label>

              <label className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200">
                <input
                  type="radio"
                  name="option_selected"
                  checked={optionSelected === 'declined'}
                  onChange={() => setOptionSelected('declined')}
                  className="w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-900">Declined / no decision</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => saveOptionsAndMaybeComplete(false)}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
              disabled={saving}
            >
              Save Draft
            </button>
            <button
              type="button"
              onClick={() => saveOptionsAndMaybeComplete(true)}
              className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
              disabled={saving}
            >
              Complete Diagnostic
            </button>
          </div>
        </div>
      )}

      {step === 'summary' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Summary</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Status</div>
              <div className="mt-2">{statusChip ? <Chip label={statusChip.label} className={statusChip.className} /> : '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Upgrades presented</div>
              <div className="mt-1 font-medium text-gray-900">{selectedUpgrades.length}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-xs text-gray-500">Option selected</div>
              <div className="mt-1 font-medium text-gray-900">{formatOptionSelectedLabel(optionSelected)}</div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm font-semibold text-gray-900">Next steps</div>
            <div className="text-sm text-gray-700 mt-1">
              This is the Phase 1 MVP flow. Next iterations will add the remaining diagnostic modules, baseline snapshot, and report generation.
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate(`/diagnostics/${encodeURIComponent(sessionId)}/presentation`)}
              className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700"
            >
              Enter Presentation Mode
            </button>
            <button
              type="button"
              onClick={() => navigate('/diagnostics')}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
            >
              Back to Diagnostics
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
